import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { AuthUser } from "@workspace/api-client-react";
import { addToWatchlist } from "@workspace/api-client-react";
import {
  fetchMe,
  getCsrf,
  csrfInit,
  signIn as apiSignIn,
  signUp as apiSignUp,
  signOut as apiSignOut,
} from "@/lib/auth-client";
import { AuthContext } from "./use-auth";
import { CLERK_PENDING_KEY, clerkSignOutBestEffort } from "@/lib/clerk";
import { useQueryClient } from "@tanstack/react-query";

interface SignInModalState {
  open: boolean;
  reason?: string;
  onSuccess?: (user: AuthUser) => void;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const identity = useRef<string | null>(null);
  const epoch = useRef(0);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [signInModal, setSignInModal] = useState<SignInModalState>({ open: false });
  const successRef = useRef<((u: AuthUser) => void) | undefined>(undefined);
  const applyUser = useCallback((next: AuthUser | null) => {
    if (identity.current !== (next?.id ?? null)) {
      epoch.current++;
      void queryClient.cancelQueries();
      queryClient.clear();
      identity.current = next?.id ?? null;
    }
    setUser(next);
  }, [queryClient]);

  const refresh = useCallback(async () => {
    const started = epoch.current;
    try {
      const me = await fetchMe();
      if (started === epoch.current) applyUser(me);
    } catch {
      if (started === epoch.current) applyUser(null);
    }
  }, [applyUser]);

  useEffect(() => {
    let cancelled = false;
    const started = epoch.current;
    (async () => {
      getCsrf().catch(() => undefined);
      try {
        const me = await fetchMe();
        if (!cancelled && started === epoch.current) applyUser(me);
      } catch {
        if (!cancelled && started === epoch.current) applyUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyUser]);

  const prevEmailVerifiedRef = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    const verified = user?.emailVerified ?? false;
    const wasVerified = prevEmailVerifiedRef.current;
    prevEmailVerifiedRef.current = verified;

    if (!verified || wasVerified === true) return;

    const pendingSlug = localStorage.getItem("kitdex_pending_watchlist");
    if (!pendingSlug) return;

    (async () => {
      try {
        const { getShirtBySlug } = await import("@workspace/api-client-react");
        const shirt = await getShirtBySlug(pendingSlug);
        const token = await getCsrf();
        await addToWatchlist(Number(shirt.id), csrfInit(token));
      } catch {
        // best-effort
      } finally {
        localStorage.removeItem("kitdex_pending_watchlist");
      }
    })();
  }, [user?.emailVerified, user?.id]);

  const completeSuccess = useCallback((u: AuthUser) => {
    epoch.current++;
    applyUser(u);
    setSignInModal((s) => ({ ...s, open: false }));
    const cb = successRef.current;
    successRef.current = undefined;
    if (cb) {
      try { cb(u); } catch { /* noop */ }
    }
  }, [applyUser]);

  const signIn = useCallback(async (email: string, password: string) => {
    const u = await apiSignIn(email, password);
    completeSuccess(u);
    return u;
  }, [completeSuccess]);

  const signUp = useCallback(async (email: string, password: string, handle: string) => {
    const u = await apiSignUp(email, password, handle);
    completeSuccess(u);
    return u;
  }, [completeSuccess]);

  const signOut = useCallback(async () => {
    epoch.current++;
    void queryClient.cancelQueries();
    queryClient.clear();
    // Clear any pending Google exchange so the bridge can't re-sign-in.
    localStorage.removeItem(CLERK_PENDING_KEY);
    // Local sign-out is guaranteed; Clerk sign-out is best-effort and never
    // throws (a lingering Clerk session alone cannot re-create kd_session).
    await apiSignOut();
    applyUser(null);
    await clerkSignOutBestEffort();
  }, [applyUser, queryClient]);

  const requireSignIn = useCallback((opts?: { reason?: string; onSuccess?: (user: AuthUser) => void }) => {
    if (user) {
      opts?.onSuccess?.(user);
      return;
    }
    successRef.current = opts?.onSuccess;
    setSignInModal({ open: true, reason: opts?.reason, onSuccess: opts?.onSuccess });
  }, [user]);

  const closeSignInModal = useCallback(() => {
    successRef.current = undefined;
    setSignInModal({ open: false });
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      signIn,
      signUp,
      signOut,
      refresh,
      completeExternalSignIn: completeSuccess,
      requireSignIn,
      closeSignInModal,
      signInModal,
    }),
    [user, loading, signIn, signUp, signOut, refresh, completeSuccess, requireSignIn, closeSignInModal, signInModal],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
